import { useAuth0 } from "@auth0/auth0-react";
import jwt_decode from "jwt-decode";
import React, { useMemo } from "react";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import { EmailVerificationOverrideSchema } from "src/types/authData";
import { z } from "zod";
import {
  loading,
  notVerified,
  ok,
  Status,
  unauthenticated,
  UserData,
} from "../../types/authData";
import { HasuraRole } from "../../types/hasuraRole";
import { Permission } from "../../types/permissions";
import useAccessToken from "../useAccessToken";
import { Email, Sms, User } from "./types";

function pickRole(assignedRoles: HasuraRole[]): HasuraRole {
  return assignedRoles.reduce((role, nextRole) => {
    if (nextRole === HasuraRole.ADMIN) return HasuraRole.ADMIN;
    if (nextRole === HasuraRole.ORG_ADMIN) return HasuraRole.ORG_ADMIN;
    if (nextRole === HasuraRole.DISTRICT_ADMIN)
      return HasuraRole.DISTRICT_ADMIN;
    if (nextRole === HasuraRole.SCHOOL_ADMIN) return HasuraRole.SCHOOL_ADMIN;
    return role;
  }, HasuraRole.USER);
}

export const jwtSchema = z.object({
  "https://hasura.io/jwt/claims": z.object({
    "x-hasura-user-id": z.string(),
    "x-hasura-user-group": z.string(),
    "x-hasura-avela-organization-ids": z.string().transform((value) =>
      value
        .replace("{", "")
        .replace("}", "")
        .split(",")
        .filter((x) => x)
    ),
    "x-hasura-avela-school-ids": z
      .string()
      .transform((value) =>
        value
          .replace("{", "")
          .replace("}", "")
          .split(",")
          .filter((x) => x)
      )
      .optional(),
    "x-hasura-avela-attending-school-ids": z
      .string()
      .transform((value) =>
        value
          .replace("{", "")
          .replace("}", "")
          .split(",")
          .filter((x) => x)
      )
      .optional(),
  }),

  "https://hasura.io/jwt/claims/assignedRoles": z.string().array().optional(),
  "https://hasura.io/jwt/claims/permissions": z.array(z.string()).optional(),
  iat: z.number(),
});

export const UserContext = React.createContext<UserData<User> | null>(null);

type Props = { children: React.ReactNode };

export const UserContextProvider = ({ children }: Props) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const token = useAccessToken();

  const userData = useMemo(() => {
    if (!isAuthenticated) return unauthenticated;
    if (token.status === Status.UNAUTHENTICATED) return unauthenticated;
    if (isNullOrUndefined(user)) return unauthenticated;
    if (isLoading) return loading;
    if (token.status === Status.LOADING) return loading;

    const jwt = jwtSchema.safeParse(jwt_decode(token.data));
    if (
      !jwt.success ||
      !isNonEmptyString(
        jwt.data["https://hasura.io/jwt/claims"]["x-hasura-user-id"]
      )
    ) {
      console.error(jwt.error);
      return unauthenticated;
    }

    const claims = jwt.data["https://hasura.io/jwt/claims"];

    const organizations = claims["x-hasura-avela-organization-ids"];
    let assignedRoles: HasuraRole[] =
      user["https://hasura.io/jwt/claims/assignedRoles"];
    if (!Array.isArray(assignedRoles)) {
      assignedRoles = [];
    }

    const userGroup = claims["x-hasura-user-group"];

    const permissions: Permission[] =
      (jwt.data[
        "https://hasura.io/jwt/claims/permissions"
      ] as unknown as Permission[]) ?? [];

    const userId = claims["x-hasura-user-id"];

    const isVerified = (user.email_verified ?? false) || isSSOUser(userId);

    const loginType: Email | Sms =
      user.email === undefined
        ? { type: "sms" }
        : {
            type: "email",
            email: user.email,
            isVerified,
          };
    const currentSchoolIds =
      claims["x-hasura-avela-attending-school-ids"] ?? [];
    const applyingSchoolIds = claims["x-hasura-avela-school-ids"] ?? [];

    const data: User = {
      id: userId,
      role: pickRole(assignedRoles),
      userGroup,
      loginType,
      organizations,
      permissions,
      currentSchoolIds,
      applyingSchoolIds,
      tokenIssuedAt: jwt.success ? jwt.data.iat : 0,
    };

    const emailVerificationOverride = EmailVerificationOverrideSchema.safeParse(
      user.emailVerificationOverride
    );
    if (
      emailVerificationOverride.success &&
      !emailVerificationOverride.data.isVerified
    ) {
      return notVerified<User>(data, emailVerificationOverride.data);
    }

    return ok(data);
  }, [isAuthenticated, isLoading, token, user]);

  return (
    <UserContext.Provider value={userData}>{children}</UserContext.Provider>
  );
};

function isSSOUser(userId: string): boolean {
  const ssoPrefix = ["waad|", "samlp|"];
  return (
    ssoPrefix.find((prefix) => {
      return userId.startsWith(prefix);
    }) !== undefined
  );
}
